import {
    assertDemoAllowed,
    createRequestFilters,
    ListProviderDocumentsRequest,
    ProviderCreateDocumentPayload,
    ProviderCreateDocumentType,
    ProviderDocument,
} from '@enter/core';
import _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import type { DocumentGenerator } from './document.generator';
import { DocumentService } from './document.service';
import { IFeatures, IItemListing, IItemListingRequest } from './root.store';
import { appendTo, parseItemListingRequest } from './util';

export class DocumentStore {
    @observable public documents: ProviderDocument[] = [];

    private features: IFeatures;
    private documentGenerator: DocumentGenerator;
    private documentService: DocumentService = new DocumentService();

    constructor(features: IFeatures, documentGenerator: DocumentGenerator) {
        this.features = features;
        this.documentGenerator = documentGenerator;
    }

    @action
    public async loadDocumentListing(ilr: IItemListingRequest): Promise<IItemListing<ProviderDocument>> {
        const lpgr = ilr2lpgr(ilr);

        const listing = await (this.features.isDemo
            ? this.documentGenerator.loadDocumentListing(lpgr)
            : this.documentService.fetchDocumentListing(lpgr));
        if (!listing) {
            throw new Error(`No documents available`);
        }
        const { items } = listing;
        runInAction(() => {
            this.documents = appendTo<ProviderDocument>(this.documents, items);
        });
        return listing;
    }

    @action
    public async uploadDocument({
        file,
        documentType,
        description,
        claimId,
    }: {
        file: File;
        documentType: ProviderCreateDocumentType;
        description?: string;
        claimId?: string;
    }): Promise<ProviderDocument> {
        if (!file) {
            throw new Error('A file is required to upload a document.');
        }
        if (!documentType) {
            throw new Error('Please choose a document type.');
        }

        if (this.features.isDemo) {
            throw new Error('Document uploads are not available in demo mode.');
        }

        const payload: ProviderCreateDocumentPayload = {
            filename: file.name,
            documentType,
            description: description || file.name,
            claimId,
        };

        const created = await this.documentService.createProviderDocument(payload);
        if (!created?.id) {
            throw new Error('Unable to create document shell.');
        }

        const upload = await this.documentService.createProviderDocumentUpload(created.id);
        if (!upload?.url) {
            throw new Error('Unable to obtain an upload URL for this document.');
        }

        await this.uploadFileToS3(upload.url, file);
        await this.documentService.refreshProviderDocumentMetadata(created.id);

        runInAction(() => {
            this.documents = appendTo<ProviderDocument>(this.documents, created);
        });

        return created;
    }

    private uploadFileToS3(url: string, file: File): Promise<void> {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('load', () => resolve());
            xhr.upload.addEventListener('error', () => reject(new Error('Failed to upload document contents.')));
            // Uploads go straight to a pre-signed URL and never touch the generated client, so the
            // demo middleware cannot see them (NS-14).
            assertDemoAllowed(url, 'PUT');
            xhr.open('PUT', url);
            xhr.setRequestHeader('x-amz-server-side-encryption', 'aws:kms');
            xhr.overrideMimeType(file.type);
            xhr.send(file);
        });
    }
}

// Convert from IItemListingRequest to ListProviderDocumentsRequest
export const ilr2lpgr = (ilr: IItemListingRequest): ListProviderDocumentsRequest => {
    const base = parseItemListingRequest(ilr) as ListProviderDocumentsRequest;
    const filters = createRequestFilters(_.get(ilr, 'filters'), true);
    return {
        ...base,
        ...filters,
    };
};
